import { Card, Divider, Checkbox } from '@care/react-component-lib';
import {
  Button,
  CardContent,
  Grid,
  Hidden,
  FormControlLabel,
  Typography,
  Box,
  type Theme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import getConfig from 'next/config';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { getMoreFilterValues, filterMultiToggleValues } from '@/utils/filters';
import { MORE_FILTERS_LIST } from '@/constants';
import { saveFilters, loadFilters } from '../../../utils/filtersStorage';

interface MoreFiltersProps {
  isClear: Boolean;
  onClose(): void;
  onChange(): void;
  enrollmentURL: string;
}

const {
  publicRuntimeConfig: { NEXT_ASSET_URL },
} = getConfig();

const MoreFilters = (props: MoreFiltersProps) => {
  const [moreFilters, setMoreFilters] = useState<string[]>([]);
  const { isClear, onChange, enrollmentURL, onClose } = props;
  const isSelected = (value: string, key: string) => {
    const filters = loadFilters();
    let existing;
    existing = moreFilters.find((element) => element === value);

    if (existing === undefined && filters !== null) {
      existing = Object.keys(filters).find((element) => element === key);
    }
    return existing !== undefined;
  };

  const [isUpdated, setIsUpdated] = useState(false);

  const handleFilterMultiToggle = (event: React.SyntheticEvent) => {
    setMoreFilters(filterMultiToggleValues(event, moreFilters));
    setIsUpdated(true);
  };

  const handleApply = () => {
    saveFilters(getMoreFilterValues(moreFilters), onChange);
    onClose();
  };

  useEffect(() => {
    if (isClear) {
      setMoreFilters([]);
    }
  }, [isClear]);

  const renderCheckbox = (value: string, label: string, disabled: boolean, key: string) => {
    return (
      <FormControlLabel
        key={value}
        control={<Checkbox disabled={disabled} name="options" checked={isSelected(value, key)} />}
        label={label}
        value={value}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row-reverse',
          justifyContent: 'space-between',

          [theme.breakpoints.up('md')]: {
            display: 'inline-flex',
            flexDirection: 'row',
          },
        })}
        labelPlacement="end"
        onChange={handleFilterMultiToggle}
      />
    );
  };

  const handleJoinNowButton = () => {
    const amplitudeData = {
      cta_text: 'Join now (CTA within "more filters")',
    };

    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };

  return (
    <Box p={4} width={{ md: '767px' }}>
      <Typography variant="h3" color="primary" py={1.25} px={0.5}>
        More Filters
      </Typography>
      <Grid>
        {MORE_FILTERS_LIST.map((moreFilter) => (
          <Grid item xs={12} md={6} py={0.5} px={{ xs: 0.5, md: 2 }} key={moreFilter.label}>
            {renderCheckbox(
              moreFilter.value,
              moreFilter.label,
              moreFilter.disabled,
              moreFilter.key
            )}
          </Grid>
        ))}
        <Hidden implementation="css" mdDown>
          <Divider />
          <Grid item xs={12} sx={{ '& > img': { opacity: 0.2 } }}>
            <img
              src={`${NEXT_ASSET_URL}/images/disabled-filters.png`}
              alt="Caregiver Details"
              width="100%"
              height="90%"
            />
          </Grid>
        </Hidden>
      </Grid>
      <Card
        careVariant="subtle"
        sx={(theme: Theme) => ({
          width: '300px',
          top: '258px',
          position: 'fixed',
          left: '37px',
          zIndex: 1,

          [theme.breakpoints.up('sm')]: {
            left: '230px',
          },
        })}>
        <CardContent sx={(theme) => ({ padding: theme.spacing(4), textAlign: 'center' })}>
          <Typography variant="body1">Create a free account to access all filters!</Typography>
          <Button
            href={enrollmentURL}
            color="primary"
            variant="contained"
            sx={(theme) => ({ marginTop: theme.spacing(4) })}
            onClick={handleJoinNowButton}>
            Join now
          </Button>
        </CardContent>
      </Card>
      <Box mt={2} textAlign="center">
        <Button
          color="secondary"
          variant="outlined"
          size="small"
          onClick={() => onClose()}
          sx={(theme) => ({ marginRight: theme.spacing(2) })}>
          Close
        </Button>
        <Button
          color="secondary"
          variant="contained"
          disabled={!isUpdated}
          onClick={handleApply}
          size="small">
          Apply
        </Button>
      </Box>
    </Box>
  );
};

export default MoreFilters;
